import { FileDown, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, tokens } from '../lib/api';
import type { Child, Transcript, TranscriptCourse } from '../lib/types';
import { useParent } from './ParentContext';

/**
 * The high-school transcript (#620), on the Portfolio tab: courses for 9th to 12th grade with level, credits and the
 * final grade; credits and GPA; filled from the grade book if the family likes; and the formal PDF.
 */
const LEVELS: { value: TranscriptCourse['level']; label: string }[] = [
  { value: 'regular', label: 'Regular' },
  { value: 'honors', label: 'Honors' },
  { value: 'ap', label: 'AP' },
  { value: 'dual', label: 'Dual enrollment' },
];
const gpa = (n: number | null) => (n === null ? '—' : n.toFixed(2));

export function TranscriptCard({ child }: { child: Child }) {
  const { toast } = useParent();
  const [t, setT] = useState<(Transcript & { enabled: true }) | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api
      .get<Transcript & { enabled: boolean }>(`/api/children/${child.id}/transcript`)
      .then((r) => setT(r.enabled ? (r as Transcript & { enabled: true }) : null))
      .catch(() => setT(null));
  }, [child.id]);
  useEffect(load, [load]);
  if (!t) return null;

  const call = async (p: Promise<Transcript & { enabled: true; added?: number }>, done?: (r: Transcript & { added?: number }) => void) => {
    try {
      const r = await p;
      setT(r);
      done?.(r);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  };
  const saveCourse = (c: TranscriptCourse) => call(api.put(`/api/children/${child.id}/transcript/courses/${c.id}`, c));
  async function pdf() {
    setBusy(true);
    try {
      const res = await fetch(`/api/children/${child.id}/transcript/pdf`, { method: 'POST', headers: { Authorization: `Bearer ${tokens.parent()}` } });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new ApiError(data?.error ?? `Couldn’t make the transcript (${res.status}).`, res.status);
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'transcript.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      toast({ text: 'The transcript is downloading.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack transcript">
      <div className="row-between wrap">
        <h2>High school transcript</h2>
        <button className="btn primary" disabled={busy || !t.years.some((y) => y.courses.length)} onClick={pdf}>
          <FileDown size={16} /> Make the PDF
        </button>
      </div>
      <div className="transcript-stats">
        <div>
          <strong>{t.totals.credits}</strong>
          <span>Credits earned</span>
        </div>
        <div>
          <strong>{gpa(t.totals.gpa)}</strong>
          <span>GPA</span>
        </div>
        <div>
          <strong>{gpa(t.totals.weighted)}</strong>
          <span>Weighted GPA</span>
        </div>
      </div>
      <div className="field-row wrap-row">
        <label>
          9th grade started
          <select
            value={t.info.ninth ?? ''}
            onChange={(e) => call(api.put(`/api/children/${child.id}/transcript`, { ninth: Number(e.target.value), graduation: `${Number(e.target.value) + 4}-05` }))}
          >
            {!t.info.ninth && <option value="">Pick a year</option>}
            {Array.from({ length: 12 }, (_, i) => new Date().getFullYear() - 6 + i).map((y) => (
              <option key={y} value={y}>
                Fall {y}
              </option>
            ))}
          </select>
        </label>
        <label>
          Graduation
          <input
            type="month"
            value={t.info.graduation}
            onChange={(e) => t.info.ninth && call(api.put(`/api/children/${child.id}/transcript`, { ninth: t.info.ninth, graduation: e.target.value }))}
          />
        </label>
      </div>
      {t.info.guessed && t.info.ninth && <p className="muted small">Worked out from {child.name}’s grade; change it if that’s wrong.</p>}

      {t.years.map((y) => (
        <div key={y.grade} className="transcript-year stack">
          <div className="row-between wrap">
            <strong>{y.label}</strong>
            <span className="row-gap">
              {y.courses.length > 0 && (
                <span className="muted small">
                  {y.totals.credits} credits · GPA {gpa(y.totals.gpa)}
                </span>
              )}
              {y.start !== null && (
                <button
                  className="btn small"
                  onClick={() =>
                    call(api.post(`/api/children/${child.id}/transcript/fill`, { grade: y.grade }), (r) =>
                      toast({ text: r.added ? `Added ${r.added} course${r.added === 1 ? '' : 's'} from the grade book. Check the levels and credits.` : 'Nothing new in the grade book for that year.', tone: r.added ? 'good' : 'info' }),
                    )
                  }
                >
                  Fill from the grade book
                </button>
              )}
            </span>
          </div>
          {y.courses.length > 0 && (
            <div className="course-grid" role="table" aria-label={y.label}>
              <span className="field-label">Course</span>
              <span className="field-label">Level</span>
              <span className="field-label">Credits</span>
              <span className="field-label">Grade</span>
              <span />
              {y.courses.map((c) => (
                <CourseRow key={c.id} c={c} onSave={saveCourse} onRemove={() => call(api.del(`/api/children/${child.id}/transcript/courses/${c.id}`))} />
              ))}
            </div>
          )}
          <button
            type="button"
            className="link-btn"
            onClick={() => call(api.post(`/api/children/${child.id}/transcript/courses`, { grade: y.grade, name: 'New course', level: 'regular', credits: t.settings.defaultCredits, mark: '' }))}
          >
            <Plus size={14} /> Add a course
          </button>
        </div>
      ))}
      <p className="muted small">
        Honors adds {t.settings.honorsBonus} and AP or dual enrollment {t.settings.apBonus} to the weighted GPA. Pass and other grades without points earn credit but don’t count
        toward GPA. With state compliance on, the legal name and date of birth on the PDF come from the child’s Profile.
      </p>
    </section>
  );
}

/** One course: edits save when a field is left. */
function CourseRow({ c, onSave, onRemove }: { c: TranscriptCourse; onSave: (c: TranscriptCourse) => void; onRemove: () => void }) {
  const [d, setD] = useState(c);
  useEffect(() => setD(c), [c]);
  const commit = (next: TranscriptCourse) => {
    setD(next);
    if (next.name.trim() && (next.name !== c.name || next.level !== c.level || next.credits !== c.credits || next.mark !== c.mark)) onSave(next);
  };
  return (
    <>
      <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} onBlur={() => commit(d)} maxLength={120} aria-label="Course" />
      <select value={d.level} onChange={(e) => commit({ ...d, level: e.target.value as TranscriptCourse['level'] })} aria-label="Level">
        {LEVELS.map((l) => (
          <option key={l.value} value={l.value}>
            {l.label}
          </option>
        ))}
      </select>
      <input type="number" min={0} max={4} step={0.25} value={d.credits} onChange={(e) => setD({ ...d, credits: Number(e.target.value) })} onBlur={() => commit(d)} aria-label="Credits" />
      <input value={d.mark} onChange={(e) => setD({ ...d, mark: e.target.value.toUpperCase() })} onBlur={() => commit(d)} maxLength={4} placeholder="A" aria-label="Grade" />
      <button type="button" className="icon-btn" aria-label={`Remove ${c.name}`} onClick={onRemove}>
        <X size={15} />
      </button>
    </>
  );
}
